import React, { useState, useEffect } from 'react';
import { Settings, Cpu, Moon, Sun, CheckCircle2, Upload, Film, Link, Sparkles, Server, Globe, Zap, Download } from 'lucide-react';
import * as api from '../../api';

export default function SettingsView({ theme, setTheme, onOpenInstallPrompt }) {
  const [selectedCity, setSelectedCity] = useState('Indore');
  const [cityVideos, setCityVideos] = useState({ Indore: '', Pune: '', Mumbai: '', Pushkar: '' });
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loadSettings = async () => {
    try {
      const res = await api.getAdminSettings();
      if (res?.success && res.data?.cityVideos) {
        setCityVideos(res.data.cityVideos);
      }
    } catch (e) {
      console.warn('Failed to load admin settings:', e);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    if (!videoFile && !videoUrlInput.trim()) {
      alert('Please select a video file or enter a valid video URL.');
      return;
    }

    setUploading(true);
    setSuccessMsg('');

    try {
      let payload;
      if (videoFile) {
        payload = new FormData();
        payload.append('city', selectedCity);
        payload.append('bannerVideo', videoFile);
      } else {
        payload = { city: selectedCity, videoUrl: videoUrlInput.trim() };
      }

      const res = await api.uploadCityVideo(payload);
      if (res?.success) {
        setSuccessMsg(`Featured hero video for ${selectedCity} published live! 🚀`);
        if (res.data?.cityVideos) {
          setCityVideos(res.data.cityVideos);
        }
        setVideoFile(null);
        setVideoUrlInput('');
        setTimeout(() => setSuccessMsg(''), 4000);
      }
    } catch (err) {
      alert(err.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const currentVideo = cityVideos[selectedCity];
  const formattedVideoUrl = currentVideo 
    ? (currentVideo.startsWith('http') ? currentVideo : `${api.API_ORIGIN || 'https://www.entermtrx.com'}${currentVideo.startsWith('/') ? currentVideo : `/${currentVideo}`}`)
    : null;

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Settings size={28} style={{ color: 'var(--neon-cyan)' }} /> Platform Settings & Admin Controls
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Upload city featured videos, system diagnostics, and interface configuration.
        </p>
      </div>

      {/* ADMIN HERO VIDEO BANNER UPLOAD & MANAGER */}
      <div className="cyber-card cyber-card-glow-pink" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', border: '1px solid rgba(246, 44, 101, 0.4)', background: 'linear-gradient(135deg, rgba(246, 44, 101, 0.05) 0%, rgba(11, 10, 20, 0.95) 100%)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 900, color: '#FFFFFF', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Film size={22} style={{ color: '#F62C65' }} /> City Featured Hero Video Banner Manager
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px', margin: 0 }}>
              Upload and publish dynamic video banners streamable by the React Native app for each city.
            </p>
          </div>
          <span style={{ padding: '4px 10px', borderRadius: '12px', background: 'rgba(246, 44, 101, 0.2)', border: '1px solid #F62C65', color: '#F62C65', fontSize: '0.75rem', fontWeight: 900 }}>
            ● ADMIN LIVE VIDEO FEED
          </span>
        </div>

        {/* Success Toast Notification */}
        {successMsg && (
          <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(0, 255, 135, 0.15)', border: '1px solid rgba(0, 255, 135, 0.4)', color: '#00FF87', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircle2 size={16} /> {successMsg}
          </div>
        )}

        {/* City Selector Tabs */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {['Indore', 'Pune', 'Mumbai', 'Pushkar'].map(city => {
            const isSelected = selectedCity === city;
            const hasVideo = !!cityVideos[city];
            return (
              <button
                key={city}
                type="button"
                onClick={() => {
                  setSelectedCity(city);
                  setVideoFile(null);
                  setVideoUrlInput('');
                }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '12px',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  border: isSelected ? '1.5px solid #F62C65' : '1px solid var(--border-color)',
                  background: isSelected ? 'rgba(246, 44, 101, 0.2)' : 'rgba(0,0,0,0.3)',
                  color: isSelected ? '#FFFFFF' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <span>{city}</span>
                {hasVideo && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#00FF87' }} />}
              </button>
            );
          })}
        </div>

        {/* Video Upload Form */}
        <form onSubmit={handleUploadVideo} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* File Upload Box */}
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                <Upload size={14} style={{ color: '#F62C65' }} /> Upload Video File (.mp4, .mov)
              </label>
              <input
                type="file"
                accept="video/*"
                className="form-control"
                style={{ padding: '8px', cursor: 'pointer' }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setVideoFile(e.target.files[0]);
                    setVideoUrlInput('');
                  }
                }}
              />
              {videoFile && (
                <div style={{ fontSize: '0.75rem', color: '#00FF87', marginTop: '4px', fontWeight: 700 }}>
                  Selected: {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                </div>
              )}
            </div>

            {/* External Video URL Box */}
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                <Link size={14} style={{ color: 'var(--neon-cyan)' }} /> Or Paste Video Stream URL
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="https://.../video.mp4"
                value={videoUrlInput}
                onChange={(e) => {
                  setVideoUrlInput(e.target.value);
                  setVideoFile(null);
                }}
              />
            </div>
          </div>

          {/* Active Preview */}
          {formattedVideoUrl && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '800', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                CURRENT FEATURED VIDEO STREAM FOR {selectedCity.toUpperCase()}:
              </div>
              <div style={{ height: '140px', borderRadius: '12px', overflow: 'hidden', background: '#000', position: 'relative', border: '1px solid rgba(255,255,255,0.1)' }}>
                <video
                  src={formattedVideoUrl}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  controls
                  muted
                  autoPlay
                  loop
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="btn"
            disabled={uploading}
            style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(90deg, #F62C65 0%, #EAB308 100%)',
              color: '#FFFFFF',
              fontWeight: 900,
              fontSize: '0.9rem',
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              border: 'none',
              marginTop: '8px',
            }}
          >
            {uploading ? (
              <span>Uploading & Processing Video...</span>
            ) : (
              <>
                <Sparkles size={16} />
                <span>PUBLISH FEATURED VIDEO FOR {selectedCity.toUpperCase()} 🚀</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* API Server Environment Switcher */}
      <div className="cyber-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Server size={20} style={{ color: 'var(--neon-purple)' }} /> API Target Environment & Failover
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
              Currently connected to: <strong style={{ color: api.isUsingLocalApi() ? 'var(--neon-emerald)' : 'var(--neon-cyan)' }}>{api.API_ORIGIN}</strong> ({api.isUsingLocalApi() ? 'Local Machine Node' : 'Cloud Gateway entermtrx.com'})
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn btn-sm ${api.getApiTarget() === 'auto' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => api.switchApiTarget('auto')}
              title="Automatically detect local server on port 5001, otherwise use cloud API"
            >
              <Zap size={14} /> Auto-Detect
            </button>
            <button
              className={`btn btn-sm ${api.getApiTarget() === 'local' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => api.switchApiTarget('local')}
              title="Force connect to local backend (http://localhost:5001)"
            >
              <Server size={14} /> Local (5001)
            </button>
            <button
              className={`btn btn-sm ${api.getApiTarget() === 'cloud' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => api.switchApiTarget('cloud')}
              title="Force connect to Cloud API (https://entermtrx.com)"
            >
              <Globe size={14} /> Cloud (entermtrx.com)
            </button>
          </div>
        </div>
      </div>

      {/* Nodes Health Card */}
      <div className="cyber-card cyber-card-glow-cyan" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Cpu size={20} style={{ color: 'var(--neon-cyan)' }} /> System Node Health & Active Services
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
          {[
            { name: 'API Server Endpoint', status: `Connected (${api.isUsingLocalApi() ? 'Local Node 5001' : 'Cloud Gateway'})`, host: `${api.BASE_URL}`, color: api.isUsingLocalApi() ? 'emerald' : 'cyan' },
            { name: 'Supabase PostgreSQL Data Store', status: 'Healthy (Realtime Active)', host: 'Supabase Cloud Node', color: 'emerald' },
            { name: 'Socket Realtime Relay', status: 'Live (Captains & Floating Bubble)', host: `${(api.API_ORIGIN || 'https://www.entermtrx.com').replace(/^http/, 'ws')}`, color: 'cyan' },
            { name: 'Media Upload Storage', status: 'Multer Disk Engine Ready', host: `${api.API_ORIGIN || 'https://www.entermtrx.com'}/uploads`, color: 'purple' },
          ].map((node, idx) => (
            <div key={idx} style={{ padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{node.name}</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: `var(--neon-${node.color})`, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckCircle2 size={12} /> {node.status}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '6px' }}>{node.host}</div>
            </div>
          ))}
        </div>
      </div>

      {/* PWA App Shortcut Installation Center */}
      <div className="cyber-card cyber-card-glow-cyan" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', border: '1px solid rgba(0, 255, 135, 0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Download size={20} style={{ color: 'var(--neon-green)' }} /> PWA & Home Screen Shortcut Installer
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
              Install the RAPMAP Admin Console as a standalone Progressive Web App (PWA) with 1-tap desktop/mobile launch.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-emerald"
            onClick={onOpenInstallPrompt}
            style={{ padding: '10px 18px', borderRadius: '10px', fontWeight: 900, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 0 15px rgba(0, 255, 135, 0.3)' }}
          >
            <Download size={16} /> Open Install Shortcut Window
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginTop: '4px' }}>
          <div style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>PWA MANIFEST STATUS</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--neon-green)', fontWeight: 800, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={13} /> Active (/manifest.json)
            </div>
          </div>
          <div style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>SERVICE WORKER ENGINE</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--neon-cyan)', fontWeight: 800, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={13} /> Registered (/service-worker.js)
            </div>
          </div>
          <div style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>RESET INSTALL PROMPT</div>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('pwa_prompt_dismissed');
                alert('Install banner reset! It will appear on next reload.');
              }}
              style={{ background: 'transparent', border: 'none', color: 'var(--neon-purple)', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', padding: 0, marginTop: '2px' }}
            >
              Reset Dismissed Banner
            </button>
          </div>
        </div>
      </div>

      {/* Theme & Display Options */}
      <div className="cyber-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Interface & Visual Theme Settings
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px' }}>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>Visual Interface Mode</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Currently using {theme === 'dark' ? 'Cyber Neon Dark Mode (Recommended)' : 'Light Mode'}</div>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => {
              const next = theme === 'dark' ? 'light' : 'dark';
              setTheme(next);
              localStorage.setItem('theme', next);
              document.documentElement.setAttribute('data-theme', next);
            }}
          >
            {theme === 'dark' ? <Sun size={16} style={{ color: 'var(--neon-amber)' }} /> : <Moon size={16} style={{ color: 'var(--neon-purple)' }} />}
            <span>Toggle {theme === 'dark' ? 'Light' : 'Cyber Dark'} Mode</span>
          </button>
        </div>
      </div>
    </div>
  );
}
